import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useFormikContext } from "formik";
import React from "react";

import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import { SkuFormData } from "@/components/screens/ProductFormPage/types";
import useToast from "@/contexts/toast-context";
import useGetSKUs from "@/hooks/requests/sku/use-get-skus";
import useDialog from "@/hooks/use-dialog";
import useSelectSearch from "@/hooks/use-select-search";
import { ISku } from "@/interfaces/sku.interface";
import { KitTableMessages } from "@/messages/products/kit-table.messages";
import { FilterType } from "@/types/filter.types";

import { buildDefaultKit } from "../../helpers";

interface Props {
  isVisible: boolean;
  onClose: () => void;
  messages: KitTableMessages;
  parentSKU: SkuFormData | null;
  isEditing: boolean;
}

const AddKitComponentDialog: React.FC<Props> = ({ isVisible, onClose, messages, parentSKU }) => {
  const { isVisible: open, handleClose, handleOpen } = useDialog();
  const { values, setFieldValue } = useFormikContext<SkuFormData>();
  const { showToast } = useToast();
  const selectSearch = useSelectSearch<ISku>();

  const {
    search,
    isLoading: isLoadingDebounce,
    onSearch,
    clearSearch,
    itemSelected: selectedSKU,
    selectItem,
  } = selectSearch;

  const { isLoading: isLoadingRequest, data } = useGetSKUs({
    pageNumber: 0,
    pageSize: 50,
    filters: [{ property: "title", filterType: FilterType.Contains, value: search }],
  });

  const isLoading = isLoadingDebounce || isLoadingRequest;
  const filteredOptions = data?.items.filter((sku) => !sku.kit) || [];

  const onConfirm = async () => {
    if (!selectedSKU) return;

    const kitExists = values.kitComponents.some(
      (kit) => kit.componentSkuId === selectedSKU.id && kit.skuId === (parentSKU?.id || 0)
    );

    if (kitExists) {
      showToast({ text: messages.duplicateKitErrorMessage, type: "error" });
      return;
    }

    const _parentSKU = parentSKU || ({ id: 0, title: "" } as SkuFormData);
    const kitComponent = {
      ...buildDefaultKit(_parentSKU, selectedSKU.id),
      referenceSkuName: selectedSKU.title,
    };

    const updatedKits = [...values.kitComponents, kitComponent];
    setFieldValue("kitComponents", updatedKits);
    onClose();
  };

  return (
    <Dialog open={isVisible} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{messages.addSKUDialogTitle}</DialogTitle>
      <DialogContent>
        <Autocomplete
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          value={selectedSKU}
          onChange={(_, itemSelected) => selectItem(itemSelected)}
          fullWidth
          options={filteredOptions}
          isOptionEqualToValue={(option, value) => option.title === value.title}
          getOptionLabel={(option) => `${option.title} - ${option.code}` || ""}
          renderOption={(props, option) => (
            <li {...props} key={`${option.id}-${option.code}`}>
              {option.title} - {option.code}
            </li>
          )}
          loading={isLoading}
          renderInput={(params) => {
            const { InputProps, ...rest } = params;

            return (
              <MDInput
                {...rest}
                label={messages.addSKUInputLabel}
                placeholder={messages.addSKUInputPlaceholder}
                onBlur={clearSearch}
                fullWidth
                variant="outlined"
                onChange={(e) => onSearch(e.target.value)}
                InputProps={{
                  ...InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            );
          }}
        />
      </DialogContent>

      <DialogActions>
        <MDButton variant="gradient" color="light" onClick={onClose}>
          {messages.cancelButton}
        </MDButton>
        <MDButton variant="gradient" color="dark" disabled={!selectedSKU} onClick={onConfirm}>
          {messages.updateButton}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddKitComponentDialog;
